// Calls the Vercel train proxies. Redis enforces the shared per-caller budget;
// this tab's queue avoids consuming it unnecessarily and can be cancelled.
import * as Sentry from "@sentry/react";

const cache = new Map<string, { data: unknown; timestamp: number }>();
const requestTimestamps: number[] = [];
const MAX_REQUESTS_PER_MINUTE = 20;

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(signal?.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener("abort", abort); resolve(); }, ms);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

async function waitForRateLimit(signal?: AbortSignal): Promise<void> {
  for (;;) {
    signal?.throwIfAborted();
    const now = Date.now();
    while (requestTimestamps.length && requestTimestamps[0] <= now - 60_000) requestTimestamps.shift();
    if (requestTimestamps.length < MAX_REQUESTS_PER_MINUTE) {
      requestTimestamps.push(now);
      return;
    }
    // Every waiter rechecks and reserves its slot after waking.
    await delay(requestTimestamps[0] + 60_000 - now + 1, signal);
  }
}

function retryAfterMs(response: Response): number {
  const retry = response.headers.get("Retry-After");
  if (retry) {
    const ms = /^\d+(\.\d+)?$/.test(retry) ? Number(retry) * 1000 : Date.parse(retry) - Date.now();
    if (Number.isFinite(ms)) return Math.max(500, ms);
  }
  const reset = Number(response.headers.get("X-RateLimit-Reset"));
  return reset > Date.now() ? reset - Date.now() : 5000;
}

function retryDelayMs(response: Response, attempt: number): number | null {
  if (response.ok || attempt >= 2) return null;
  if (response.status !== 429 && response.status < 500) return null;
  const wait = response.status === 429 || response.headers.has("Retry-After")
    ? retryAfterMs(response) : 1000 * 2 ** attempt;
  // Leave long server-imposed windows to the caller instead of retrying early.
  return wait <= 60_000 ? wait : null;
}

async function readResponse<T>(endpoint: string, response: Response, attempt: number): Promise<T> {
  Sentry.addBreadcrumb({ category: "api", message: `${endpoint} → ${response.status}`,
    level: response.ok ? "info" : "error", data: { status: response.status, retries: attempt } });
  if (!response.ok) {
    throw Object.assign(new Error(response.status === 429 ? "rate_limited" : `API error ${response.status}`), {
      status: response.status, retryAt: Date.now() + retryAfterMs(response),
    });
  }
  return response.json() as Promise<T>;
}

export async function apiFetch<T>(
  endpoint: string,
  params: Record<string, string> = {},
  cacheMaxAgeMs = 5 * 60 * 1000,
  signal?: AbortSignal,
): Promise<T> {
  signal?.throwIfAborted();
  const url = new URL(`/api${endpoint}`, globalThis.location.origin);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  url.searchParams.sort();
  const key = url.toString();
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cacheMaxAgeMs) return cached.data as T;
  cache.delete(key);

  for (let attempt = 0; ; attempt++) {
    await waitForRateLimit(signal);
    const timeout = AbortSignal.timeout(30_000);
    const response = await fetch(key, {
      headers: { Accept: "application/json" },
      signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    });
    const wait = retryDelayMs(response, attempt);
    if (wait !== null) {
      await response.body?.cancel();
      await delay(wait, signal);
      continue;
    }
    const data = await readResponse<T>(endpoint, response, attempt);
    signal?.throwIfAborted();
    if (cache.size >= 300) cache.delete(cache.keys().next().value!);
    cache.set(key, { data, timestamp: Date.now() });
    return data;
  }
}
